import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { listVehicles, createLicense } from "@/services/vehicles";
import { vehicleOpsApi } from "@/services/vehicleOps";
import type { LicenseType } from "@/services/types/vehicles";
import { uploadReceipt } from "@/services/uploads";
import { AuthedImage } from "@/shared/ui/AuthedImage";

const TYPES: { value: LicenseType; icon: string; label: string; sub: string }[] = [
  { value: "cua", icon: "🚌", label: "CUA", sub: "商业用户授权" },
  { value: "dot_authorization", icon: "🇺🇸", label: "DOT", sub: "联邦运输证" },
  { value: "commercial_insurance", icon: "🛡", label: "商业保险", sub: "年付" },
  { value: "coi", icon: "🛡", label: "COI", sub: "保险证书" },
  { value: "annual_inspection", icon: "🔍", label: "年检", sub: "State Inspection" },
  { value: "registration", icon: "📋", label: "注册", sub: "Registration" },
  { value: "other", icon: "➕", label: "自定义类型", sub: "如第三方营运证 / 园区准入证" },
];
const ALERTS = [
  { key: "a60", dot: "🟡", title: "60 天预警", to: "车务管理员", ch: "邮件 + 微信", forced: false },
  { key: "a30", dot: "🔴", title: "30 天预警", to: "车务 + 老板", ch: "邮件 + 短信 + 微信", forced: false },
  { key: "expired", dot: "⚫", title: "已过期", to: "全员相关", ch: "调度自动 hold", forced: true },
];
const inp = "w-full rounded-lg border border-slate-300 px-3 py-2 text-[14px]";

export function VehicleOpsLicenseForm() {
  const nav = useNavigate();
  const vehQ = useQuery({ queryKey: ["vehicles", "all"], queryFn: () => listVehicles() });
  const licQ = useQuery({ queryKey: ["vehicle-ops", "licenses"], queryFn: () => vehicleOpsApi.listFleetLicenses() });
  const [vehicleId, setVehicleId] = useState("");
  const [type, setType] = useState<LicenseType>("coi");
  const [customName, setCustomName] = useState("");
  const [number, setNumber] = useState("");
  const [issuer, setIssuer] = useState("");
  const [issueDate, setIssueDate] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [documentUrl, setDocumentUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(false);
  const [alertOn, setAlertOn] = useState<Record<string, boolean>>({ a60: true, a30: true, expired: true });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onPickScan(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setError(null); setUploading(true);
    try {
      const r = await uploadReceipt(f, f.name || "license.jpg");
      setDocumentUrl(r.url);
    } catch (err) { setError("扫描件上传失败：" + (err as Error).message); }
    finally { setUploading(false); if (e.target) e.target.value = ""; }
  }
  const back = () => nav("/admin/vehicle-operations/expenses");

  // 每车证照齐全度
  const typeCount = useMemo(() => {
    const m = new Map<string, Set<string>>();
    for (const l of licQ.data ?? []) {
      if (!m.has(l.vehicle_id)) m.set(l.vehicle_id, new Set());
      m.get(l.vehicle_id)!.add(l.license_type);
    }
    return m;
  }, [licQ.data]);

  // 有效期 / 剩余 自动计算
  const calc = useMemo(() => {
    if (!issueDate || !expiryDate) return null;
    const months = Math.round((new Date(expiryDate).getTime() - new Date(issueDate).getTime()) / (1000 * 3600 * 24 * 30.4));
    const days = Math.round((new Date(expiryDate).getTime() - Date.now()) / (1000 * 3600 * 24));
    const warn = days < 0 ? "⚫ 已过期" : days <= 30 ? "🔴 30 天内" : days <= 60 ? "🟡 60 天内" : "✅ 正常";
    return { months, days, warn };
  }, [issueDate, expiryDate]);

  async function submit() {
    setError(null);
    if (!vehicleId) { setError("请选择车辆"); return; }
    if (!number.trim() || !issuer.trim() || !issueDate || !expiryDate) { setError("证照号/颁发机构/颁发日/到期日必填"); return; }
    if (type === "other" && !customName.trim()) { setError("自定义类型需填名称"); return; }
    setSubmitting(true);
    try {
      await createLicense(vehicleId, {
        license_type: type, custom_type_name: type === "other" ? customName : undefined,
        license_number: number, issuer, issue_date: issueDate, expiry_date: expiryDate,
        document_url: documentUrl || undefined,
      });
      back();
    } catch (e: unknown) { setError((e as Error).message); } finally { setSubmitting(false); }
  }

  return createPortal(
    <div className="fixed inset-0 z-[100] flex flex-col bg-slate-50">
      <header className="flex items-center justify-between border-b border-slate-200 bg-white px-6 py-3.5">
        <div><h1 className="text-[17px] font-bold text-slate-900">📋 录入证照</h1><p className="text-[12px] text-slate-500">7 类平等处理 · 到期日驱动 60/30 双层预警（实时计算·无 cron）</p></div>
        <button onClick={back} className="rounded-lg border border-slate-300 px-3.5 py-2 text-[13px] font-medium hover:bg-slate-50">✕ 取消</button>
      </header>

      <div className="flex-1 overflow-y-auto px-8 py-6">
        <div className="flex flex-col gap-5">
          {/* 选车 */}
          <Section title="🚐 选择车辆 *">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              {(vehQ.data ?? []).map((v) => {
                const n = typeCount.get(v.id)?.size ?? 0;
                return (
                  <button key={v.id} onClick={() => setVehicleId(v.id)} className={`rounded-lg border-2 p-3 text-left ${vehicleId === v.id ? "border-blue-600 bg-blue-50" : "border-slate-200"}`}>
                    <div className="text-[13px] font-semibold text-slate-900">{v.vehicle_number} · {v.model_label_cn}</div>
                    <div className="text-[11px] text-slate-500">当前 {n} 类证照{n >= 7 ? "齐全" : ` · 缺 ${7 - n}`}</div>
                  </button>
                );
              })}
            </div>
          </Section>

          {/* 证照类型 */}
          <Section title="🏷 证照类型 *">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {TYPES.map((t) => (
                <button key={t.value} onClick={() => setType(t.value)} className={`rounded-lg border-2 p-3 text-left ${type === t.value ? "border-blue-600 bg-blue-50" : "border-slate-200"}`}>
                  <div className="text-[13px] font-semibold">{t.icon} {t.label}</div>
                  <div className="text-[10px] text-slate-500">{t.sub}</div>
                </button>
              ))}
            </div>
            {type === "other" && <Field label="自定义类型名称 *"><input value={customName} onChange={(e) => setCustomName(e.target.value)} placeholder="如 第三方营运证" className={inp} /></Field>}
          </Section>

          {/* 详情 */}
          <Section title="📝 证照详情">
            <div className="grid grid-cols-2 gap-4">
              <Field label="证照号 *"><input value={number} onChange={(e) => setNumber(e.target.value)} placeholder="YST-2027-XXXX" className={inp} /></Field>
              <Field label="颁发机构 *"><input value={issuer} onChange={(e) => setIssuer(e.target.value)} placeholder="Montana DOT" className={inp} /></Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="颁发日 *"><input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} className={inp} /></Field>
              <Field label="到期日 *（驱动预警）"><input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} className={inp} /></Field>
            </div>
            {calc && (
              <div className="grid grid-cols-2 gap-4">
                <Stat label="有效期（自动计算）" value={`${calc.months} 个月`} />
                <Stat label="当前剩余（自动计算）" value={`${calc.days < 0 ? `逾期 ${-calc.days}` : calc.days} 天 · ${calc.warn}`} />
              </div>
            )}
          </Section>

          {/* 证照照片（普通拍照·非财务凭证不用扫描仪）*/}
          <Section title="📷 证照照片">
            {documentUrl ? (
              <div className="flex items-center gap-3">
                <div className="relative h-24 w-24 shrink-0">
                  <button type="button" onClick={() => setLightbox(true)} className="group h-full w-full overflow-hidden rounded-lg border border-slate-200" title="点击放大">
                    <AuthedImage src={documentUrl} alt="证照" className="h-full w-full object-cover" />
                    <span className="absolute inset-0 hidden items-center justify-center bg-black/40 text-[11px] font-semibold text-white group-hover:flex">🔍 放大</span>
                  </button>
                  <button type="button" onClick={() => setDocumentUrl("")} title="删除"
                    className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-rose-500 text-sm font-bold leading-none text-white shadow hover:bg-rose-600">×</button>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-[13px] font-semibold text-emerald-700">✓ 已上传</span>
                  <button onClick={() => setLightbox(true)} className="text-left text-[12px] text-blue-600 hover:underline">🔍 放大查看</button>
                  <label className="cursor-pointer text-left text-[12px] text-blue-600 hover:underline">重新拍照<input type="file" accept="image/*" capture="environment" className="hidden" onChange={onPickScan} /></label>
                  <button onClick={() => setDocumentUrl("")} className="text-left text-[12px] text-rose-600 hover:underline">✕ 删除</button>
                </div>
              </div>
            ) : (
              <label className="flex cursor-pointer flex-col items-center gap-1 rounded-2xl border-2 border-dashed border-slate-300 py-6 hover:bg-slate-50">
                <span className="text-2xl">{uploading ? "⏳" : "📷"}</span>
                <span className="text-[13px] font-semibold text-blue-600">{uploading ? "上传中…" : "拍照 / 选择照片"}</span>
                <span className="text-[11px] text-slate-500">证照存档照片（JPG/PNG）</span>
                <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onPickScan} disabled={uploading} />
              </label>
            )}
          </Section>

          {/* 预警设置 */}
          <Section title="🔔 预警设置（60/30 双层 + 过期）">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              {ALERTS.map((a) => (
                <div key={a.key} className="rounded-lg border border-slate-200 p-3">
                  <div className="text-[13px] font-semibold">{a.dot} {a.title}</div>
                  <div className="mt-1 text-[11px] text-slate-500">通知：{a.to}</div>
                  <div className="text-[11px] text-slate-500">{a.ch}</div>
                  <label className="mt-2 flex items-center gap-1.5 text-[12px] font-medium text-slate-700">
                    <input type="checkbox" checked={alertOn[a.key]} disabled={a.forced} onChange={(e) => setAlertOn((p) => ({ ...p, [a.key]: e.target.checked }))} className="h-4 w-4" />
                    {a.forced ? "强制启用（不可关）" : "启用"}
                  </label>
                </div>
              ))}
            </div>
          </Section>

          {error && <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[13px] text-rose-700">{error}</div>}
        </div>
      </div>

      <footer className="flex items-center justify-end gap-2 border-t border-slate-200 bg-white px-8 py-3.5">
        <button onClick={back} className="rounded-lg border border-slate-300 px-4 py-2 text-[13px] font-medium hover:bg-slate-50">取消</button>
        <button onClick={submit} disabled={submitting} className="rounded-lg bg-blue-600 px-5 py-2 text-[13px] font-semibold text-white hover:bg-blue-700">{submitting ? "保存中…" : "保存证照"}</button>
      </footer>

      {lightbox && documentUrl && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/80 p-4" onClick={() => setLightbox(false)}>
          <AuthedImage src={documentUrl} alt="证照放大" className="max-h-[92vh] max-w-[92vw] rounded-lg bg-white object-contain shadow-2xl" onClick={(e) => e.stopPropagation()} />
          <button onClick={() => setLightbox(false)} className="absolute right-5 top-5 text-3xl leading-none text-white/80 hover:text-white">×</button>
        </div>
      )}
    </div>,
    document.body,
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return <div className="rounded-xl border border-slate-200 bg-white p-6"><div className="mb-3 text-[14px] font-semibold text-slate-900">{title}</div><div className="flex flex-col gap-4">{children}</div></div>;
}
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="flex flex-col gap-1.5"><span className="text-[13px] font-medium text-slate-700">{label}</span>{children}</label>;
}
function Stat({ label, value }: { label: string; value: string }) {
  return <div className="rounded-lg bg-slate-50 px-3 py-2"><div className="text-[11px] text-slate-500">{label}</div><div className="text-[14px] font-bold text-slate-900">{value}</div></div>;
}
